/**
 * The phone's back/edge-swipe navigation — one place that knows how a route
 * walks "one level up", what an open overlay is, and how a horizontal gesture is
 * told apart from a scroll.
 *
 * Why this exists (card t_6a634e11): on a phone the only way out of a page was
 * the drawer, and the Android hardware back key had no listener at all — it fell
 * through to Capacitor's default (webview history), which on the launcher root
 * does nothing and inside a hub can drop the user out of the app. Three things
 * are wired here:
 *
 *  1. `useMobileBack()` — the arrow the shell paints as the leading control of
 *     the phone header, and the "one level up" walk it performs. The arrow is
 *     only shown when the current route is deeper than its section root, so a
 *     section root (the hub dashboard, `/home`, a settings directory) keeps the
 *     leading slot free.
 *  2. `installMobileBackNavigation()` — one `App.addListener('backButton')`,
 *     Capacitor only: close what is open first, else one level up, else — and
 *     ONLY on the launcher root — let the activity finish with `App.exitApp()`.
 *     A hardware back inside a hub therefore never drops the user out of the app.
 *  3. `installMobileEdgeSwipe()` — left-edge swipe right opens the navigation
 *     drawer, right-edge swipe left goes back one step. Plain pointer events on
 *     document, decided at `pointerup`: nothing calls `preventDefault`, nothing
 *     sets `touch-action`, so a vertical drag keeps scrolling the page it always
 *     did.
 */
import { computed, type ComputedRef } from 'vue';
import { useRoute, useRouter, type Router } from 'vue-router';
import { isCapacitor } from '../config/runtime';
import { useUIStore } from '../stores/ui';
import { useRoomsNav } from './useRoomsNav';
import { useSettingsNav } from './useSettingsNav';
import { tt } from '../utils/consoleText';
import { dismissBackdrop } from '../utils/backdropDismiss';
import {
  SWIPE_AXIS_RATIO,
  SWIPE_EDGE_PX,
  SWIPE_MIN_DX,
  classifyEdgeSwipe,
  isLauncherRoot,
  isSectionRoot,
  resolveMobileParent,
  type SwipeVerdict,
} from '../utils/mobileBackRoute';

// The route table and the gesture thresholds are pure and separately tested
// (`utils/mobileBackRoute.ts` + its test) — re-exported here so a caller has one
// import, and so the store-dependent code below stays out of the test runner.
export {
  SWIPE_AXIS_RATIO,
  SWIPE_EDGE_PX,
  SWIPE_MIN_DX,
  classifyEdgeSwipe,
  isLauncherRoot,
  isSectionRoot,
  resolveMobileParent,
};
export type { SwipeVerdict };

/**
 * The top-most *visible* dismissable overlay, i.e. the thing a tap on the
 * backdrop would close: the app's modals and drawers all paint a full-screen
 * `fixed inset-0` layer with a translucent black backdrop, and every one of them
 * closes itself when that backdrop is tapped — 67 components on `@click.self`,
 * 28 on `@mousedown.self`. Tapping that element is therefore the same gesture
 * the user makes — no per-modal registry to keep in sync, and a modal that does
 * not use the pattern is simply not matched.
 *
 * The tap has to be a *tap* (see `dismissBackdrop`): the `@mousedown.self` half
 * of those overlays never hears a bare `.click()`, which is why hardware BACK
 * silently did nothing over the device sheet (card t_9e54d28c).
 */
function topVisibleBackdrop(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const candidates = [
    ...document.querySelectorAll<HTMLElement>('[class*="fixed"][class*="inset-0"][class*="bg-black"]'),
  ].filter((el) => {
    if (el.getClientRects().length === 0) return false;
    const style = getComputedStyle(el);
    return style.visibility !== 'hidden' && style.pointerEvents !== 'none';
  });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    const za = Number(getComputedStyle(a).zIndex) || 0;
    const zb = Number(getComputedStyle(b).zIndex) || 0;
    return za - zb;
  });
  return candidates[candidates.length - 1];
}

/**
 * Is the phone navigation drawer actually on screen? It is `md:hidden`, so above
 * the md breakpoint there is nothing to open — the shell keeps no off-canvas
 * drawer there (its section navigation is the docked column in landscape and the
 * BOTTOM BAR in portrait, card t_f6d03638) — and toggling the store there leaves
 * it stuck open, which in turn disables every gesture that treats an open drawer
 * as "close it first" (measured on the 800x1333 tablet: one left-edge swipe and
 * the back swipe stopped working).
 */
function mobileDrawerRendered(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(min-width: 768px)').matches;
}

/** Drawers and modals the UI store owns. */
export function closeStoredOverlays(): boolean {
  const ui = useUIStore();
  const rooms = useRoomsNav();
  const settings = useSettingsNav();

  if (ui.mobileSidebarOpen && mobileDrawerRendered()) {
    ui.closeMobileSidebar();
    return true;
  }
  if (ui.userDrawerOpen) {
    ui.closeUserDrawer();
    return true;
  }
  if (ui.statusDrawerOpen) {
    ui.closeStatusDrawer();
    return true;
  }
  if (ui.playlistDrawerOpen) {
    ui.closePlaylistDrawer();
    return true;
  }
  if (ui.siteDrawerOpen) {
    ui.closeSiteDrawer();
    return true;
  }
  if (ui.deviceControlModalDevice) {
    ui.closeDeviceControlModal();
    return true;
  }
  // The section rails (rooms list, settings tree) are drawers wherever they are
  // not the docked column — portrait included (card t_d9971280). `isDockedColumn`
  // is the shape decision itself, so this never asks whether a drawer exists.
  if (!rooms.isDockedColumn.value && rooms.sidebarOpen.value) {
    rooms.closeSidebar();
    return true;
  }
  if (!settings.isDockedColumn.value && settings.sidebarOpen.value) {
    settings.closeSidebar();
    return true;
  }
  return false;
}

/** True while a modal, drawer or edit mode is open — gestures stand down then. */
export function hasBlockingOverlay(): boolean {
  const ui = useUIStore();
  if (ui.sidebarEditMode) return true;
  if (ui.mobileSidebarOpen && mobileDrawerRendered()) return true;
  if (ui.userDrawerOpen || ui.statusDrawerOpen || ui.playlistDrawerOpen || ui.siteDrawerOpen) return true;
  if (ui.deviceControlModalDevice) return true;
  try {
    const rooms = useRoomsNav();
    const settings = useSettingsNav();
    if (!rooms.isDockedColumn.value && rooms.sidebarOpen.value) return true;
    if (!settings.isDockedColumn.value && settings.sidebarOpen.value) return true;
  } catch {
    // stores not installed yet — no drawer is open either
  }
  return topVisibleBackdrop() !== null;
}

/**
 * Close whatever is open, in the order a user would: the navigation drawer
 * first (it is the outermost), then the side drawers, then a modal's backdrop —
 * which is *tapped* (`dismissBackdrop`), not merely `.click()`ed, or the
 * `@mousedown.self` overlays would never hear it (card t_9e54d28c).
 */
export function dismissTopmost(): boolean {
  if (closeStoredOverlays()) return true;
  const backdrop = topVisibleBackdrop();
  if (backdrop) {
    dismissBackdrop(backdrop);
    return true;
  }
  return false;
}

// `useRoute()`/`useRouter()` only resolve inside a component's setup. The
// gesture and hardware-back handlers fire long after that, so the shell hands
// them the router once (from `useMobileBack()`), and `activeRouter()`/`activePath()`
// read it — falling back to the running app's own router, because a caller that
// asks for these *after* mount (as `App.vue` once did, from `onMounted`) gets
// nothing back and must not be able to blank the wiring.
let mobileRouter: Router | null = null;
let currentPath: (() => string) | null = null;

type VueAppLike = { config?: { globalProperties?: Record<string, unknown> } };

/**
 * The live router: the one handed over in setup, else the running app's. Called
 * only from handlers, never during render.
 */
export function activeRouter(): Router | null {
  if (mobileRouter) return mobileRouter;
  const app = (typeof document === 'undefined' ? null : document.querySelector('#app')) as
    (Element & { __vue_app__?: VueAppLike }) | null;
  const router = app?.__vue_app__?.config?.globalProperties?.$router as Router | undefined;
  if (router) mobileRouter = router;
  return mobileRouter;
}

/** The current route path, from the setup route when there is one. */
export function activePath(): string {
  if (currentPath) return currentPath();
  return activeRouter()?.currentRoute.value.path ?? '/';
}

/** Walk one level up from the live route, if it has a parent. */
export async function walkUpOneLevel(): Promise<boolean> {
  const parent = resolveMobileParent(activePath());
  const router = activeRouter();
  if (!parent || !router) return false;
  await router.push(parent);
  return true;
}

/** The header's back arrow: shown only below a section root, phones only. */
export function useMobileBack(): {
  showMobileBack: ComputedRef<boolean>;
  mobileBackLabel: ComputedRef<string>;
  goBackOneLevel: () => Promise<boolean>;
} {
  const route = useRoute();
  const router = useRouter();
  if (route && router) {
    mobileRouter = router;
    currentPath = () => route.path;
  }

  const parent = computed(() => resolveMobileParent(route?.path ?? '/'));
  const showMobileBack = computed(() => parent.value !== null);
  const mobileBackLabel = computed(() => tt('Back', 'Nazaj'));

  return { showMobileBack, mobileBackLabel, goBackOneLevel: walkUpOneLevel };
}

/**
 * One document-level listener per input stream. The gesture is judged from the
 * whole travel when it ends, never move-by-move: deciding mid-drag is what makes
 * a swipe handler eat a scroll.
 *
 * Why touch AND pointer, measured (`scripts/probe-swipe-events.mjs`, 390x844
 * touch context, room page): every touch drag produces
 *
 *   pointerdown → touchstart → pointermove → touchmove → pointercancel
 *   → touchmove … → touchend
 *
 * — the browser claims the gesture for scrolling and the *pointer* stream dies at
 * the first move, while the touch stream runs to the end. An earlier version of
 * this function judged the swipe at `pointerup` and therefore never handled a
 * single real gesture; `touchend` is where a touch swipe can be decided. Pointer
 * events stay for a pen (a mouse is ignored — a drag with a pressed button is not
 * a swipe), and touch pointers are left to the touch path alone so nothing is
 * handled twice.
 */
export function installMobileEdgeSwipe(): () => void {
  if (typeof document === 'undefined') return () => {};

  type Gesture = { x: number; y: number };
  let pointerGesture: (Gesture & { pointerId: number }) | null = null;
  let touchGesture: Gesture | null = null;
  let touchLast: Gesture | null = null;

  /**
   * A gesture may start here only if nothing is open and no ancestor owns the
   * horizontal axis: a dashboard grid, a floor plan or a table that scrolls
   * sideways must scroll, not navigate.
   */
  const canStartSwipe = (target: EventTarget | null): boolean => {
    if (hasBlockingOverlay()) return false;
    for (let el = target as HTMLElement | null; el && el !== document.body; el = el.parentElement) {
      const style = getComputedStyle(el);
      if (/(auto|scroll)/.test(style.overflowX) && el.scrollWidth > el.clientWidth + 4) return false;
      if (el.dataset?.mobileBackIgnore === 'true') return false;
    }
    return true;
  };

  const applyVerdict = (verdict: SwipeVerdict) => {
    if (verdict === 'none') return;
    if (hasBlockingOverlay()) return;
    if (verdict === 'open-menu') {
      // Nothing to open above md: the drawer is the phone's navigation, and at
      // ≥768px the navigation is already in the page header (see
      // `mobileDrawerRendered`).
      if (!mobileDrawerRendered()) return;
      useUIStore().toggleMobileSidebar();
      return;
    }
    void walkUpOneLevel();
  };

  const onPointerDown = (event: PointerEvent) => {
    if (event.pointerType === 'mouse' || event.pointerType === 'touch') return;
    if (!canStartSwipe(event.target)) {
      pointerGesture = null;
      return;
    }
    pointerGesture = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
  };

  const onPointerUp = (event: PointerEvent) => {
    const gesture = pointerGesture;
    pointerGesture = null;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    applyVerdict(classifyEdgeSwipe(gesture.x, event.clientX - gesture.x, event.clientY - gesture.y, window.innerWidth));
  };

  const onTouchStart = (event: TouchEvent) => {
    if (event.touches.length !== 1) {
      touchGesture = null;
      touchLast = null;
      return;
    }
    const touch = event.touches[0];
    if (!canStartSwipe(event.target)) {
      touchGesture = null;
      touchLast = null;
      return;
    }
    touchGesture = { x: touch.clientX, y: touch.clientY };
    touchLast = { ...touchGesture };
  };

  const onTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touchGesture || !touch) return;
    touchLast = { x: touch.clientX, y: touch.clientY };
  };

  /** `touchend` with the position the finger stopped at; a cancel uses the last known one. */
  const finishTouch = (event: TouchEvent) => {
    const gesture = touchGesture;
    const last = touchLast;
    touchGesture = null;
    touchLast = null;
    if (!gesture || !last) return;
    const touch = event.changedTouches[0];
    const end = touch ? { x: touch.clientX, y: touch.clientY } : last;
    applyVerdict(classifyEdgeSwipe(gesture.x, end.x - gesture.x, end.y - gesture.y, window.innerWidth));
  };

  const onScrollLikeCancel = () => {
    // Not a real cancel: the browser handed the gesture to the scroller. The
    // classifier still decides (`|dx| > |dy| * 1.5`), so a vertical drag that got
    // cancelled resolves to 'none' and a swipe that got cancelled still counts.
    const gesture = touchGesture;
    const last = touchLast;
    touchGesture = null;
    touchLast = null;
    if (!gesture || !last) return;
    applyVerdict(classifyEdgeSwipe(gesture.x, last.x - gesture.x, last.y - gesture.y, window.innerWidth));
  };

  const listeners = [
    ['pointerdown', onPointerDown],
    ['pointerup', onPointerUp],
    [
      'pointercancel',
      () => {
        pointerGesture = null;
      },
    ],
    ['touchstart', onTouchStart],
    ['touchmove', onTouchMove],
    ['touchend', finishTouch],
    ['touchcancel', onScrollLikeCancel],
  ] as const;
  for (const [type, handler] of listeners) {
    document.addEventListener(type, handler as EventListener, { passive: true, capture: true });
  }

  return () => {
    for (const [type, handler] of listeners) {
      document.removeEventListener(type, handler as EventListener, { capture: true });
    }
  };
}

/**
 * The Android hardware back key, registered once. Capacitor only — on the web
 * the browser owns the back button and the history already works.
 */
export function installMobileBackNavigation(): () => void {
  if (!isCapacitor()) return () => {};

  let removed = false;
  let handle: { remove: () => Promise<void> } | null = null;

  const onBack = async () => {
    if (dismissTopmost()) return;
    if (await walkUpOneLevel()) return;
    if (isLauncherRoot(activePath())) {
      const { App } = await import('@capacitor/app');
      await App.exitApp();
    }
    // Inside a hub on a section root: swallow it. Leaving the hub is the
    // drawer's (or the app icon's) job, never the hardware key's.
  };

  void import('@capacitor/app')
    .then(({ App }) =>
      App.addListener('backButton', () => {
        void onBack();
      }),
    )
    .then((listener) => {
      if (removed) void listener.remove();
      else handle = listener;
    })
    .catch(() => {
      // Plugin missing (web build, or a shell without @capacitor/app): the
      // hardware key then keeps its platform default.
    });

  return () => {
    removed = true;
    void handle?.remove();
  };
}
